import { __read, __assign } from 'tslib';
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import cuid from 'cuid';
import { useDetectInputMethod } from '../hooks/useDetectInputMethod.js';
import '@reach/auto-id';
import '@emotion/react';
import 'lodash.get';
import '@compono/ui-theme';
import 'deepmerge';
import '@emotion/is-prop-valid';
import { useUpdateEffect } from '../hooks/useUpdateEffect/index.js';
import { useSelectableStateMachine } from './useSelectableStateMachine.js';

var useSelectable = function (props) {
    var id = props.id, highlightSelected = props.highlightSelected, labelledby = props.labelledby, look = props.look, mount = props.mount, multipleP = props.multiple, optionsP = props.options, required = props.required, _a = props.scrollActiveIntoView, scrollActiveIntoView = _a === void 0 ? true : _a, selectActive = props.selectActive, size = props.size, onChange = props.onChange, onSelectAction = props.onSelectAction, onActiveChange = props.onActiveChange, onDone = props.onDone, _b = props.selectRef, selectRef = _b === void 0 ? React.useRef(null) : _b, triggerRef = props.triggerRef;
    var _c = __read(useState(id || cuid), 1), selectId = _c[0];
    var isUsingKeyboard = useDetectInputMethod().isUsingKeyboard;
    var listRef = React.useRef(null);
    var _d = useSelectableStateMachine({
        multiple: multipleP,
        options: optionsP,
        selectId: selectId,
        selectActive: selectActive,
        triggerRef: triggerRef,
        onSelect: onSelectAction,
    }), state = _d.state, send = _d.send;
    var onFocus = function (event) {
        var _a;
        if (((_a = event.currentTarget) === null || _a === void 0 ? void 0 : _a.nodeName) === "UL") {
            send(isUsingKeyboard() ? "KEYBOARD_FOCUS" : "FOCUS");
        }
    };
    var handleClick = useCallback(function (e) {
        var _a, _b;
        if (state.matches("active") &&
            !((_a = selectRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)) &&
            !((_b = listRef.current) === null || _b === void 0 ? void 0 : _b.contains(e.target))) {
            send("BLUR");
        }
    }, [state]);
    useEffect(function () {
        document.addEventListener("mousedown", handleClick);
        return function () {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [handleClick]);
    var _e = state.context, activeOption = _e.activeOption, options = _e.options, multiple = _e.multiple;
    // keep history of prevOptions
    var prevOptionsP = useRef(optionsP);
    var selectedOptions = useMemo(function () {
        return options.filter(function (option) { return option.selected; });
    }, [options]);
    useUpdateEffect(function () {
        if (multiple) {
            onChange === null || onChange === void 0 ? void 0 : onChange(selectedOptions);
        }
        else if (optionsP === prevOptionsP.current) {
            onChange === null || onChange === void 0 ? void 0 : onChange(selectedOptions[0]);
        }
        // intentionally wait to update prevOptionsP until here to avoid losing the change to component updates
        prevOptionsP.current = optionsP;
    }, [selectedOptions]);
    useUpdateEffect(function () {
        if (activeOption) {
            onActiveChange === null || onActiveChange === void 0 ? void 0 : onActiveChange(activeOption);
        }
    }, [activeOption]);
    var keyDownHandler = function (event) {
        var key = event.key;
        switch (key) {
            case "ArrowDown":
            case "ArrowUp":
            case "End":
            case "Enter":
            case "Home":
                event.preventDefault();
                send("keyDown".concat(key));
                break;
            case "Escape":
                event.preventDefault();
                send("keyDown".concat(key));
                onDone && onDone();
                break;
            case "Tab":
                send("keyDown".concat(key));
                break;
            case " ":
                event.preventDefault();
                send("keyDownSpace");
                break;
        }
    };
    var handleMouseEvent = function (event) {
        event.preventDefault();
        send(event);
    };
    var toggleOptionSelection = function (option) {
        send(__assign({ type: "toggleOptionSelection" }, option));
    };
    var setOptions = function (newOptions) {
        send({ type: "setOptions", options: newOptions });
    };
    return {
        activeOption: activeOption,
        keyDownHandler: keyDownHandler,
        handleMouseEvent: handleMouseEvent,
        highlightSelected: highlightSelected,
        labelledby: labelledby,
        listRef: listRef,
        look: look,
        mount: mount,
        multiple: multiple,
        onFocus: onFocus,
        options: options,
        required: required,
        scrollActiveIntoView: scrollActiveIntoView,
        selectedOptions: selectedOptions,
        selectId: selectId,
        selectRef: selectRef,
        size: size,
        state: state,
        toggleOptionSelection: toggleOptionSelection,
        setOptions: setOptions,
    };
};

export { useSelectable };
//# sourceMappingURL=useSelectable.js.map
